import React from 'react';
import { Dialog, DialogContent, Button } from '@leadsnavi/ui-kit';
import { X, AlertCircle, ChevronRight } from 'lucide-react';
import { TruncatedTextWithTooltip } from '@/components/truncated-text-with-tooltip';
import { IApiInquiryDto } from '@/services/swagger/easourcing/models';
import { ModalComponentProps } from '@/components/moda-lInvoker';
import { calcInqiryProgress, computationText, isInComputation } from '@/features/inquiries/components/inquiry-card';

export type ActiveTaskLimitResult = { type: 'replace'; inquiryId: string } | { type: 'save' };

export interface ActiveTaskLimitModalProps extends Omit<ModalComponentProps, 'onOk'> {
  runningInquiries: IApiInquiryDto[];
  loading?: boolean;
  showSaveOption?: boolean;
  // 命令式调用：支持返回结果对象
  onOk?: (result: ActiveTaskLimitResult) => void;
  // 向后兼容：支持旧的声明式调用方式
  onReplace?: (inquiryId: string) => void | Promise<void>;
  onSave?: () => void | Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

const ActiveTaskLimitModal: React.FC<ActiveTaskLimitModalProps> = ({
  open,
  visible,
  runningInquiries,
  onOk,
  onCancel,
  loading = false,
  showSaveOption = true,
  // 向后兼容的 props
  onReplace,
  onSave,
  onOpenChange,
}) => {
  const isOpen: boolean = Boolean(open ?? visible ?? false);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleReplace = async (inquiryId: string) => {
    // 优先使用命令式调用方式（ModalService）
    if (onOk) {
      onOk({ type: 'replace', inquiryId });
    }
    // 向后兼容：支持旧的声明式调用方式
    else if (onReplace) {
      await onReplace(inquiryId);
    }
  };

  const handleSave = async () => {
    // 优先使用命令式调用方式（ModalService）
    if (onOk) {
      onOk({ type: 'save' });
    }
    // 向后兼容：支持旧的声明式调用方式
    else if (onSave) {
      await onSave();
    }
  };

  const handleClose = () => {
    // 优先使用命令式调用方式（ModalService）
    if (typeof onCancel === 'function') {
      onCancel();
    }
    // 向后兼容：支持旧的声明式调用方式
    else if (typeof onOpenChange === 'function') {
      onOpenChange(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (!open) {
          handleClose();
        } else if (typeof onOpenChange === 'function') {
          // 向后兼容：支持旧的声明式调用方式
          onOpenChange(open);
        }
      }}
    >
      <DialogContent className="max-w-[460px] p-0 border-none" showCloseButton={false}>
        <div className="relative">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>

          {/* Content */}
          <div className="p-6">
            {/* Header */}
            <div className="flex flex-col items-center mb-6">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mb-4">
                <AlertCircle className="h-4 w-4 text-orange-500" />
              </div>
              <h2 className="text-[20px] font-semibold text-[#171717] mb-2">Active Task Limit Reached</h2>
              <p className="text-[#737373] text-[12px] text-center">
                You've reached the limit of <span className="font-bold text-[#262626]">3 active tasks</span>. To
                maintain sourcing quality, how would you like to handle this new inquiry?
              </p>
            </div>

            {/* Replace an active task section */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Replace an active task:</h3>
              <div className="space-y-3">
                {runningInquiries.map(inquiry => {
                  const progress = calcInqiryProgress(inquiry);
                  return (
                    <div
                      key={inquiry.inquiryId}
                      className="bg-gray-50 border border-[#E5E5E5] rounded-lg p-4 flex items-center justify-between gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <TruncatedTextWithTooltip
                          text={inquiry.inquiryName}
                          className="font-medium text-sm text-[#262626] mb-1"
                        />
                        <div className="text-sm text-[#737373] mb-2">Started {formatDate(inquiry.startTimestamp)}</div>
                        {isInComputation(inquiry) ? (
                          <span className="text-[color:var(--600-fill-,#525252)] [font-family:Geist] text-xs font-normal leading-[150%] ">
                            {computationText}
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-[#525252]">
                              {progress}%{/* {inquiry.sentCount}/{inquiry.totalCount} Sent */}
                            </span>
                            <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[200px]">
                              <div
                                className="bg-gray-400 h-2 rounded-full transition-all"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReplace(inquiry.inquiryId)}
                        disabled={loading}
                        className="flex-shrink-0"
                      >
                        Stop & launch
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Save to Session & Hold section */}
            {showSaveOption && (
              <div
                className="rounded-xl border p-4 flex items-center justify-between cursor-pointer hover:bg-[#F0FFF7] transition-colors"
                style={{
                  borderRadius: '12px',
                  border: '1px solid rgba(167, 243, 208, 0.80)',
                  background: '#F6FFFB',
                  padding: '12px 16px',
                }}
                onClick={handleSave}
              >
                <div className="flex flex-col">
                  <span className="font-medium mb-1 text-[#065F46]">Save to Session & Hold</span>
                  <span className="text-sm text-[#065F4680]">Save data in session and launch it later</span>
                </div>
                <ChevronRight color="#065F46" className="h-5 w-5 flex-shrink-0" />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActiveTaskLimitModal;
export * from './service';
